import { useId, useState, type ReactNode } from "react";
import { Info } from "lucide-react";
import {
    autoUpdate,
    flip,
    FloatingPortal,
    offset,
    shift,
    size,
    useDismiss,
    useFloating,
    useInteractions,
    useRole,
} from "@floating-ui/react";

export type ToolDescriptionPopoverProps = {
    children: ReactNode;
    /** Short label for assistive tech (e.g. "Flow Agents tool description"). */
    dialogLabel: string;
};

export function ToolDescriptionPopover({
    children,
    dialogLabel,
}: ToolDescriptionPopoverProps) {
    const [open, setOpen] = useState(false);
    const titleId = useId();

    const { refs, floatingStyles, context } = useFloating({
        open,
        onOpenChange: setOpen,
        placement: "bottom-start",
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(8),
            flip(),
            shift({ padding: 8 }),
            size({
                padding: 8,
                apply({ availableWidth, availableHeight, elements }) {
                    Object.assign(elements.floating.style, {
                        maxWidth: `${Math.min(360, availableWidth)}px`,
                        // Cap to the space on the chosen side so a panel opened
                        // near the bottom edge scrolls instead of clipping off.
                        maxHeight: `${availableHeight}px`,
                    });
                },
            }),
        ],
    });

    // Floating UI handles the dismiss behaviour the manual version did by hand:
    // outside-press closes it, Escape closes it.
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: "dialog" });
    const { getReferenceProps, getFloatingProps } = useInteractions([
        dismiss,
        role,
    ]);

    return (
        <div className="tool-description-popover">
            <button
                ref={refs.setReference}
                type="button"
                className="tool-description-popover__trigger"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                aria-haspopup="dialog"
                aria-controls={open ? titleId : undefined}
                aria-label="Show tool description"
                {...getReferenceProps()}
            >
                <Info
                    size={18}
                    strokeWidth={2}
                    className="tool-description-popover__trigger-icon"
                    aria-hidden
                />
            </button>
            {open && (
                <FloatingPortal>
                    <div
                        ref={refs.setFloating}
                        id={titleId}
                        className="tool-description-popover__panel"
                        aria-label={dialogLabel}
                        aria-modal="true"
                        style={{ ...floatingStyles, zIndex: 10050 }}
                        {...getFloatingProps()}
                    >
                        <div className="tool-description-popover__body">
                            {children}
                        </div>
                    </div>
                </FloatingPortal>
            )}
        </div>
    );
}
